import { useCallback, useEffect, useState, useSyncExternalStore } from "react";
import { getState, subscribe } from "./vault";
import { portfolioValue, refreshPrices } from "./prices";

const STALE_MS = 5 * 60 * 1000;

function useOnline() {
  const [online, setOnline] = useState(navigator.onLine);
  useEffect(() => {
    const on = () => setOnline(true);
    const off = () => setOnline(false);
    window.addEventListener("online", on);
    window.addEventListener("offline", off);
    return () => {
      window.removeEventListener("online", on);
      window.removeEventListener("offline", off);
    };
  }, []);
  return online;
}

// Live wealth: accounts, cached prices and the current total. Refreshes prices
// when they're older than five minutes and the phone is online.
export default function useWealth() {
  const state = useSyncExternalStore(subscribe, getState);
  const online = useOnline();
  const [refreshing, setRefreshing] = useState(false);
  const [lastError, setLastError] = useState(null);

  const accounts = state?.accounts || [];
  const { quotes = {}, fetchedAt = null } = state?.prices || {};
  const hasLiveHoldings = accounts.some((a) => a.holdings.some((h) => h.kind !== "cash"));

  const refresh = useCallback(async () => {
    if (!navigator.onLine) return;
    setRefreshing(true);
    try {
      const { failed } = await refreshPrices();
      setLastError(failed.length ? `Couldn't update ${failed.join(" or ")}` : null);
    } catch {
      setLastError("Couldn't update prices");
    } finally {
      setRefreshing(false);
    }
  }, []);

  useEffect(() => {
    if (online && hasLiveHoldings && (!fetchedAt || Date.now() - fetchedAt > STALE_MS)) {
      refresh();
    }
    // Only re-check when connectivity or holdings change, not on every price write.
  }, [online, hasLiveHoldings]);

  return {
    accounts,
    quotes,
    fetchedAt,
    online,
    refreshing,
    lastError,
    hasLiveHoldings,
    total: portfolioValue(accounts, quotes),
    refresh,
  };
}
